// src/components/blog/components/BlogAuthor.tsx

import Image from 'next/image'
import Link from 'next/link'
import { cn } from '~/lib/utils'
import type { Author } from '~/lib/blog/types/blog.types'

/**
 * Props for the BlogAuthor component
 * @interface BlogAuthorProps
 * @property {Author} author - Author information including name, role, image, etc.
 * @property {'sm' | 'md' | 'lg'} [size='md'] - Size variant for the author avatar
 * @property {boolean} [showRole=true] - Whether to show the author's role
 * @property {boolean} [showBio=false] - Whether to show the author's bio
 * @property {string} [className] - Additional classes for the container
 * @property {string} [imageClassName] - Additional classes for the image
 * @property {string} [textClassName] - Additional classes for the text
 * @property {boolean} [inheritTextColor=false] - Whether to inherit text color from parent
 */
interface BlogAuthorProps {
  /** Author information */
  author: Author
  /** Size variant for the author avatar */
  size?: 'sm' | 'md' | 'lg'
  /** Whether to show the author's role */
  showRole?: boolean
  /** Whether to show the author's bio */
  showBio?: boolean
  /** Additional classes for the container */
  className?: string
  /** Additional classes for the image */
  imageClassName?: string
  /** Additional classes for the text */
  textClassName?: string
  /** Whether to inherit text color from parent */
  inheritTextColor?: boolean
}

const sizeConfig = {
  sm: {
    image: 'h-8 w-8',
    name: 'text-sm',
    role: 'text-xs'
  },
  md: {
    image: 'h-10 w-10', 
    name: 'text-base',
    role: 'text-sm'
  },
  lg: {
    image: 'h-12 w-12',
    name: 'text-lg', 
    role: 'text-base'
  }
}

/**
 * Displays author information with avatar, name, and optional role
 * 
 * Features:
 * - Responsive avatar sizes (sm, md, lg)
 * - Optional clickable author name linking to profile
 * - Optional role display
 * - Optional bio display
 * - Consistent styling with rounded avatar and text formatting
 * - Dark mode support
 * - Color inheritance control
 * 
 * @component BlogAuthor
 * @example
 * ```tsx
 * // Basic usage
 * <BlogAuthor author={authorData} />
 * 
 * // With custom size and bio
 * <BlogAuthor 
 *   author={authorData} 
 *   size="lg"
 *   showBio={true}
 * />
 * 
 * // With inherited text color
 * <BlogAuthor
 *   author={authorData}
 *   inheritTextColor={true}
 * />
 * ```
 */
export function BlogAuthor({
  author,
  size = 'md',
  showRole = true,
  showBio = false,
  className,
  imageClassName,
  textClassName,
  inheritTextColor = false
}: BlogAuthorProps): JSX.Element {
  const sizes = sizeConfig[size]

  return (
    <div className={cn("flex items-center gap-x-4", className)}>
      <Link href={author.href} className="relative shrink-0">
        <Image
          src={author.imageUrl}
          alt={author.name}
          width={size === 'lg' ? 48 : size === 'md' ? 40 : 32}
          height={size === 'lg' ? 48 : size === 'md' ? 40 : 32}
          className={cn(
            sizes.image,
            "rounded-full bg-gray-100 object-cover dark:bg-gray-800",
            imageClassName
          )}
        />
      </Link>
      <div className={cn("min-w-0", textClassName)}>
        <Link 
          href={author.href}
          className={cn(
            sizes.name,
            "font-semibold",
            inheritTextColor 
              ? "text-inherit hover:text-inherit/75"
              : "text-gray-900 hover:text-gray-600 dark:text-white dark:hover:text-gray-300"
          )}
        >
          {author.name}
        </Link>
        {showRole && (
          <p className={cn(
            sizes.role,
            "truncate",
            inheritTextColor
              ? "text-inherit/75"
              : "text-gray-600 dark:text-gray-400"
          )}>
            {author.role}
          </p>
        )}
        {showBio && author.bio && (
          <p className={cn(
            "mt-1 text-sm line-clamp-2",
            inheritTextColor
              ? "text-inherit/75"
              : "text-gray-600 dark:text-gray-400"
          )}>
            {author.bio}
          </p>
        )}
      </div>
    </div>
  )
}